"use client";

import React from "react";
import { FeefoReviews } from "@/types/dto/destinations-page/destination-page-types";
import { Box, Stack, useMediaQuery, SxProps } from "@mui/material";
import { SectionHeader } from "@/components/common/section-header";
import {ColletteButton, ColletteCarousel} from "@collette-travel/collette-common-controls";
import {chunkArray} from "@/utils/general-util";
import SinglePageWrapper from "@/components/carousel/single-page-wrapper";
import FeefoReviewCard from "./feefo-review-card";
import FeefoReviewsSummary from "./feefo-reviews-summary";

interface ReviewSectionProps {
  section: FeefoReviews;
  moduleRankingPosition?: string;
  isProductClickable?: boolean;
  desktopCardsPerPage?: 2 | 4;
  headerSx?: SxProps;
  copySx?: SxProps;
}

const FeefoReviewsSection = ({
  section,
  moduleRankingPosition,
  isProductClickable = true,
  desktopCardsPerPage = 2,
  headerSx,
  copySx
}: ReviewSectionProps) => {
  const isLg = useMediaQuery((theme) => theme.breakpoints.up("lg"));
  const cardsPerPage = isLg ? desktopCardsPerPage : 1;
  return (
    <Box
      data-track="visibility"
      data-parent-container-type="reviews"
      data-module-name="Reviews Section"
      data-ranking-algorithm="manual"
      data-module-ranking-position={moduleRankingPosition}
      className="fluid-box anchor-section"
      sx={{ backgroundColor: "background.warm_strong" }}
      id="feefo-reviews"
      component="section"
    >
      <Stack
        className="destination-page-container"
        sx={{
          gap: "4rem",
          padding: {
            zero: "4rem 2.4rem",
            legacyMobile: "4rem 2.4rem",
            legacyTablet: "6rem clamp(2.4rem, 4.5vw, 6.5rem)"
          },
          backgroundColor: "background.warm_strong"
        }}
      >
        <SectionHeader
          headerText={section.basicInfo.headerText}
          headerTestId="feefo-reviews-header"
          copyText={section.basicInfo.copyText}
          copyTestId="feefo-reviews-copy"
          headerSx={headerSx}
          copySx={copySx}
        />
        <ColletteCarousel
          testId="feefo-reviews"
          variant="full-width"
          arrowPosition={isLg ? "sides" : "bottom"}
        >
          {chunkArray(section.feefoReviews, cardsPerPage).map((page, pageIndex) => {
            if (isLg && desktopCardsPerPage === 4) {
              return (
                <Box
                  key={`${page?.[0]?.id}_page`}
                  data-testid={`carousel-single-page_${pageIndex}`}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gap: "20px",
                    width: "100%",
                    flexShrink: 0,
                    alignItems: "stretch"
                  }}
                >
                  {page.map((review, cardIndex) => (
                    <FeefoReviewCard
                      review={review}
                      index={`${pageIndex}_${cardIndex}`}
                      key={review.id}
                      isProductClickable={isProductClickable}
                    />
                  ))}
                </Box>
              );
            }

            return (
              <SinglePageWrapper
                key={`${page?.[0]?.id}_page`}
                index={pageIndex}
                spacing={20}
              >
                {page.map((review, cardIndex) => (
                  <FeefoReviewCard
                    review={review}
                    index={`${pageIndex}_${cardIndex}`}
                    key={review.id}
                    isProductClickable={isProductClickable}
                  />
                ))}
              </SinglePageWrapper>
            );
          })}
        </ColletteCarousel>
        <Stack gap="2.4rem" alignItems="center">
          <FeefoReviewsSummary summary={section.feefoSummary} />
          {section.basicInfo.callToActionText && section.basicInfo.callToActionUrl && (
            <ColletteButton
              component="a"
              label={section.basicInfo.callToActionText}
              href={section.basicInfo.callToActionUrl}
              testId="feefo-reviews_cta"
              data-track="click"
              variant="outlined"
              size="large"
              sx={{
                alignSelf: "center",
                backgroundColor: "common.white"
              }}
            />
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default FeefoReviewsSection;
